import type { MetaArgs, MetaDescriptor } from 'react-router';
import type { StaticParentLoaders } from '~/modules/seo/util/merge-route-meta';
import type { loader } from '~/routes/_app.professionals.$';
import {
    getCanonicalHreflang,
    createMetaTitle,
    createMetaDescription,
    createOGTags,
} from '~/modules/seo';
import { createFAQSchema } from '~/modules/seo/schema/faq-page-schema';
import { createLDSchema } from '~/modules/seo/util/create-ld-schema';
import { createProfessionalsItemListSchema } from '~/modules/seo/schema/professionals-schema';
import {
    noIndexProfessionalCategory,
    isLocationIndexPage,
} from '~/modules/seo/util/no-index-professional-category';
import { buildLocationAwareMeta } from '~/modules/professionals-category/util/location-meta';
import type { B2BRootLoaderData } from '~/utils/business-subdomain';
import { isB2BRootLoaderData } from '~/utils/business-subdomain';
import { profsConfig } from '@modules/b2b/config/profs.config';

type ProfessionalsMetaArgs = MetaArgs<typeof loader, StaticParentLoaders>;

/**
 * Title/description/canonical for the business subdomain's B2B "Become a Pro"
 * page, which is served from this route at `/professionals`.
 */
const buildB2BMetaDescriptors = (
    props: ProfessionalsMetaArgs,
    data: B2BRootLoaderData,
): MetaDescriptor[] => {
    const title = createMetaTitle(props, [profsConfig.meta.title, 'ArchiPro']);
    const description = createMetaDescription([profsConfig.meta.description]);
    return [
        { title },
        { name: 'description', content: description },
        ...createOGTags({ meta: props, title, description }),
        {
            tagName: 'link',
            rel: 'canonical',
            href: `${data.businessOrigin}/professionals`,
        },
    ];
};

/**
 * Builds the appended meta for the professionals directory route: location-aware
 * title/description fallbacks (CMS localisation still wins), Open Graph,
 * canonical/hreflang, ItemList + FAQ structured data, and thin-location noindex.
 * The B2B subdomain page is handled separately above.
 */
export const buildProfessionalsMetaDescriptors = (
    props: ProfessionalsMetaArgs,
): MetaDescriptor[] => {
    const { data } = props;

    if (isB2BRootLoaderData(data)) {
        return buildB2BMetaDescriptors(props, data);
    }

    // Location-aware fallbacks so each category × location and hub page has a
    // distinct title/description (CMS localisation still takes precedence). Only
    // path-based location pages qualify; `?location=` query filters are excluded.
    const isLocationPage = !!data?.isLocationPage;
    const { titleFallback, descriptionFallback } = buildLocationAwareMeta({
        category: data?.directoryPageMeta?.category,
        appliedLocation: isLocationPage ? data?.appliedLocation : undefined,
        isHub: !!data?.locationHub,
    });

    const title = createMetaTitle(
        props,
        [data?.localisation?.Title, titleFallback],
        true,
        data?.pagination?.page,
    );

    const description = createMetaDescription([
        data?.localisation?.Description,
        descriptionFallback,
    ]);

    const productsListSchema = createProfessionalsItemListSchema(props);
    const FAQSchema = createFAQSchema(data?.localisation?.FAQSchemas ?? []);
    const ldSchema = createLDSchema([FAQSchema, productsListSchema ?? []]);

    const { indexPage, country } = isLocationIndexPage(data?.url ?? '');

    // Backend indexability verdict, shared with the sitemap and the link mesh: a location
    // combo page is only indexable when it has unique SEO content AND enough supply
    // (computed once in MySQL via ComboIndexability). Fails open (`undefined`/`null`) so a
    // transient GraphQL outage cannot mass-deindex healthy location pages.
    const indexableVerdict = data?.localisation?.IsIndexableFilterPage;
    const forceNoIndex = isLocationPage && indexableVerdict === false;

    const metaDescriptors = [
        { title },
        { name: 'description', content: description },
        ...createOGTags({ meta: props, title, description }),
        ...getCanonicalHreflang(props, {
            noAlternateLinks: indexPage,
            professionalCountry: country,
        }),
        ...ldSchema,
    ];

    return noIndexProfessionalCategory(
        metaDescriptors,
        data?.url ?? '',
        forceNoIndex,
    );
};
